/* Created by Interactive 63 - https://interactive63.com */
/**
 * Colour parsing, formatting and interpolation. Interpolation uses premultiplied alpha in sRGB,
 * which is how browsers animate legacy colour syntaxes.
 */
import { COLOUR_NAMES } from './colour-names';
import { formatNumber } from './easing/format-number';

export interface RGBA {
  /** 0..255 */
  r: number;
  g: number;
  b: number;
  /** 0..1 */
  a: number;
}

const NUMBER = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/;

/** Limits a number to an inclusive range. */
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Parses a plain number token, or null when it is not one. */
function parseNumber(token: string): number | null {
  return NUMBER.test(token) ? Number(token) : null;
}

/** Parses a channel that is either a number or a percentage of `scale`. */
function parseChannel(token: string, scale: number): number | null {
  if (token.endsWith('%')) {
    const percent = parseNumber(token.slice(0, -1));
    return percent === null ? null : (percent / 100) * scale;
  }
  return parseNumber(token);
}

/** Parses a hue in degrees, accepting deg, grad, rad and turn units. */
function parseHue(token: string): number | null {
  const match = /^([+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?)(deg|grad|rad|turn)?$/.exec(token);
  if (!match) return null;
  const value = Number(match[1]);
  switch (match[2]) {
    case 'grad':
      return value * 0.9;
    case 'rad':
      return (value * 180) / Math.PI;
    case 'turn':
      return value * 360;
    default:
      return value;
  }
}

/** Converts hue (degrees), saturation and lightness (0..1) to 0..255 RGB. */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const hue = (((h % 360) + 360) % 360) / 360;
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  /** One RGB channel for a hue offset. */
  const channel = (offset: number): number => {
    let t = hue + offset;
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [channel(1 / 3) * 255, channel(0) * 255, channel(-1 / 3) * 255];
}

/** Parses #rgb, #rgba, #rrggbb and #rrggbbaa. */
function parseHex(hex: string): RGBA | null {
  if (!/^[0-9a-f]+$/.test(hex)) return null;
  const digits =
    hex.length === 3 || hex.length === 4
      ? (hex.match(/./g) ?? []).map((c) => c + c)
      : hex.length === 6 || hex.length === 8
        ? (hex.match(/../g) ?? [])
        : null;
  if (!digits) return null;
  const [r = 0, g = 0, b = 0, a = 255] = digits.map((d) => parseInt(d, 16));
  return { r, g, b, a: a / 255 };
}

/** Parses the arguments of rgb(), rgba(), hsl() and hsla(). */
function parseFunction(name: string, body: string): RGBA | null {
  const args = body.split(/[\s,/]+/).filter((token) => token !== '');
  if (args.length !== 3 && args.length !== 4) return null;
  const alphaToken = args[3];
  const alpha = alphaToken === undefined ? 1 : parseChannel(alphaToken, 1);
  if (alpha === null) return null;
  const [first, second, third] = args;
  if (first === undefined || second === undefined || third === undefined) return null;

  if (name === 'rgb' || name === 'rgba') {
    const r = parseChannel(first, 255);
    const g = parseChannel(second, 255);
    const b = parseChannel(third, 255);
    if (r === null || g === null || b === null) return null;
    return { r: clamp(r, 0, 255), g: clamp(g, 0, 255), b: clamp(b, 0, 255), a: clamp(alpha, 0, 1) };
  }

  const hue = parseHue(first);
  const saturation = parseChannel(second.endsWith('%') ? second : `${second}%`, 1);
  const lightness = parseChannel(third.endsWith('%') ? third : `${third}%`, 1);
  if (hue === null || saturation === null || lightness === null) return null;
  const [r, g, b] = hslToRgb(hue, clamp(saturation, 0, 1), clamp(lightness, 0, 1));
  return { r, g, b, a: clamp(alpha, 0, 1) };
}

/**
 * Parses a CSS colour. Returns null for values that cannot be interpolated, such as `none`,
 * `currentColor`, `inherit`, `url(...)` and anything unrecognised.
 */
export function parseColour(input: string): RGBA | null {
  const text = input.trim().toLowerCase();
  if (text === 'transparent') return { r: 0, g: 0, b: 0, a: 0 };
  if (text.startsWith('#')) return parseHex(text.slice(1));
  const fn = /^(rgba?|hsla?)\(([^()]*)\)$/.exec(text);
  if (fn?.[1] !== undefined && fn[2] !== undefined) return parseFunction(fn[1], fn[2]);
  if (Object.hasOwn(COLOUR_NAMES, text)) {
    const value = COLOUR_NAMES[text] ?? 0;
    return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255, a: 1 };
  }
  return null;
}

/** Formats a colour as `#rrggbb`, or `rgb(r g b / a)` when it is not fully opaque. */
export function formatColour(colour: RGBA): string {
  const [r, g, b] = [colour.r, colour.g, colour.b].map((c) => Math.round(clamp(c, 0, 255)));
  const alpha = clamp(colour.a, 0, 1);
  if (alpha === 1) {
    return `#${[r, g, b].map((c) => (c ?? 0).toString(16).padStart(2, '0')).join('')}`;
  }
  return `rgb(${r} ${g} ${b} / ${formatNumber(alpha, 3)})`;
}

/** Formats parseable colours canonically and returns anything else trimmed but unchanged. */
export function normaliseColour(input: string): string {
  const parsed = parseColour(input);
  return parsed ? formatColour(parsed) : input.trim();
}

/**
 * Interpolates between two colours at progress p using premultiplied alpha. If either side
 * cannot be parsed the result is discrete: `a` before the halfway point, otherwise `b`.
 */
export function interpolateColour(a: string, b: string, p: number): string {
  const from = parseColour(a);
  const to = parseColour(b);
  if (!from || !to) return p < 0.5 ? a : b;

  const alpha = from.a + (to.a - from.a) * p;
  /** Interpolates one premultiplied channel and returns it un-premultiplied. */
  const channel = (x: number, y: number): number =>
    alpha <= 0 ? 0 : (x * from.a + (y * to.a - x * from.a) * p) / alpha;
  return formatColour({
    r: channel(from.r, to.r),
    g: channel(from.g, to.g),
    b: channel(from.b, to.b),
    a: alpha,
  });
}
